import React, { useState, useEffect } from 'react';
import { useTheme, getT, getBrandColors} from '../ThemeContext';
import API_BASE from '../apiConfig';
import { getSessionUser } from '../utils/session';
import { FaTrophy, FaHistory } from 'react-icons/fa';
import { useSort } from '../hooks/useSort';
import { useDateTimeFormat } from '../utils/formatters';

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";

const TournamentStudentTab = ({ studentId, studentName }) => {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { fmtDate } = useDateTimeFormat();
  const user = getSessionUser();

  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isAdmin = user?.user_role === 'Admin' || user?.user_role === 'Super Admin';

  // Standard rule: every table column is sortable — click to sort, click again to flip.
  // Placed here (before the `if (loading) return` below) so the Rules of Hooks hold.
  const regSort = useSort(registrations, "registration_date", "desc");

  useEffect(() => {
    if (studentId) {
      fetchTournamentHistory();
    }
  }, [studentId]);

  const fetchTournamentHistory = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BASE}/tournament/registrations/student/${studentId}`);
      const data = await response.json();

      if (data.success) {
        setRegistrations(data.registrations || []);
      } else {
        setError(data.message || 'Error loading tournament history');
      }
    } catch (err) {
      setError('Failed to load tournament data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status, paymentStatus) => {
    if (paymentStatus === 'COMPLETED') {
      return { bg: 'rgba(52,211,153,0.12)', text: getBrandColors().green, border: 'rgba(52,211,153,0.30)' };
    } else if (status === 'CANCELLED' || status === 'VOIDED') {
      return { bg: 'rgba(248,113,113,0.12)', text: getBrandColors().red, border: 'rgba(248,113,113,0.30)' };
    } else if (status === 'APPROVED') {
      return { bg: 'rgba(96,165,250,0.12)', text: '#60a5fa', border: 'rgba(96,165,250,0.30)' };
    }
    return { bg: 'rgba(156,163,175,0.12)', text: '#9ca3af', border: 'rgba(156,163,175,0.30)' };
  };

  const getStatusLabel = (status, paymentStatus) => {
    return paymentStatus === 'COMPLETED' ? '✓ Paid' : status;
  };

  if (loading) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        color: T.textMuted,
        fontSize: '14px',
        fontFamily: FONT
      }}>
        Loading tournament history...
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '13px 18px',
          borderRadius: '10px',
          marginBottom: '20px',
          fontSize: '14px',
          fontWeight: 600,
          fontFamily: FONT,
          background: 'rgba(248,113,113,0.12)',
          color: getBrandColors().red,
          border: '1px solid rgba(248,113,113,0.30)',
          backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
          WebkitBackdropFilter: BLUR
        }}>
          ❌ {error}
        </div>
      )}

      {registrations.length === 0 ? (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          background: T.glass,
          backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
          WebkitBackdropFilter: BLUR,
          border: T.glassBdr,
          borderRadius: '10px',
          color: T.textMuted,
          fontFamily: FONT
        }}>
          <FaHistory size={28} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p style={{ margin: 0, fontSize: '14px' }}>No tournament registrations yet</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
            fontFamily: FONT
          }}>
            <thead>
              <tr style={{ background: T.glass, borderBottom: `2px solid ${T.inputBdr}` }}>
                <th style={{
                  padding: '12px',
                  textAlign: 'left',
                  color: T.textPrimary,
                  fontWeight: 700,
                  fontSize: '11px',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  ...regSort.thSort('tournament_name', T)
                }} onClick={() => regSort.toggleSort('tournament_name')}>
                  Tournament{regSort.sortArrow('tournament_name')}
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: T.textPrimary,
                  fontWeight: 700,
                  fontSize: '11px',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  ...regSort.thSort('age_category', T)
                }} onClick={() => regSort.toggleSort('age_category')}>
                  Category{regSort.sortArrow('age_category')}
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: T.textPrimary,
                  fontWeight: 700,
                  fontSize: '11px',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  ...regSort.thSort('number_of_days_selected', T)
                }} onClick={() => regSort.toggleSort('number_of_days_selected')}>
                  Days{regSort.sortArrow('number_of_days_selected')}
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'right',
                  color: T.textPrimary,
                  fontWeight: 700,
                  fontSize: '11px',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  ...regSort.thSort('total_cost', T)
                }} onClick={() => regSort.toggleSort('total_cost')}>
                  Cost{regSort.sortArrow('total_cost')}
                </th>
                <th style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: T.textPrimary,
                  fontWeight: 700,
                  fontSize: '11px',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  ...regSort.thSort('registration_status', T)
                }} onClick={() => regSort.toggleSort('registration_status')}>
                  Status{regSort.sortArrow('registration_status')}
                </th>
                {isAdmin && (
                  <th style={{
                    padding: '12px',
                    textAlign: 'center',
                    color: T.textPrimary,
                    fontWeight: 700,
                    fontSize: '11px',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    ...regSort.thSort('payment_status', T)
                  }} onClick={() => regSort.toggleSort('payment_status')}>
                    Payment{regSort.sortArrow('payment_status')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {regSort.sorted.map((reg, idx) => {
                const statusColor = getStatusColor(reg.registration_status, reg.payment_status);

                return (
                  <tr
                    key={reg.registration_id}
                    style={{
                      background: idx % 2 === 0 ? 'transparent' : T.glass,
                      borderBottom: `1px solid ${T.inputBdr}`,
                      color: T.textPrimary
                    }}
                  >
                    <td style={{
                      padding: '12px',
                      fontWeight: 600,
                      fontSize: '13px'
                    }}>
                      <div style={{ fontWeight: 700 }}>{reg.tournament_name}</div>
                      <div style={{
                        fontSize: '11px',
                        color: T.textMuted,
                        marginTop: '3px'
                      }}>
                        {reg.registration_date ? fmtDate(reg.registration_date) : ''}
                      </div>
                    </td>
                    <td style={{
                      padding: '12px',
                      textAlign: 'center'
                    }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        backgroundColor: 'rgba(247,206,11,0.12)',
                        color: getBrandColors().yellow,
                        fontSize: '12px',
                        fontWeight: 700,
                        border: '1px solid rgba(247,206,11,0.30)'
                      }}>
                        {reg.age_category}
                      </span>
                    </td>
                    <td style={{
                      padding: '12px',
                      textAlign: 'center',
                      color: T.textPrimary,
                      fontSize: '13px'
                    }}>
                      {reg.number_of_days_selected} / {reg.tournament_days || '?'}
                    </td>
                    <td style={{
                      padding: '12px',
                      textAlign: 'right',
                      fontWeight: 700,
                      fontSize: '13px'
                    }}>
                      ${reg.total_cost ? reg.total_cost.toFixed(2) : '0.00'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 700,
                        backgroundColor: statusColor.bg,
                        color: statusColor.text,
                        border: `1px solid ${statusColor.border}`
                      }}>
                        {getStatusLabel(reg.registration_status, reg.payment_status)}
                      </span>
                    </td>
                    {isAdmin && (
                      <td style={{
                        padding: '12px',
                        textAlign: 'center',
                        fontSize: '12px'
                      }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '6px',
                          backgroundColor: reg.payment_status === 'COMPLETED'
                            ? 'rgba(52,211,153,0.12)'
                            : 'rgba(245,158,11,0.12)',
                          color: reg.payment_status === 'COMPLETED' ? getBrandColors().green : '#f59e0b',
                          fontWeight: 700,
                          border: reg.payment_status === 'COMPLETED'
                            ? '1px solid rgba(52,211,153,0.30)'
                            : '1px solid rgba(245,158,11,0.30)'
                        }}>
                          {reg.payment_status}
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{
        marginTop: '20px',
        padding: '14px 16px',
        background: T.glass,
        backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
        WebkitBackdropFilter: BLUR,
        border: `1.5px solid ${T.inputBdr}`,
        borderRadius: '10px',
        borderLeft: `4px solid ${T.yellow}`,
        color: T.textMuted,
        fontSize: '12px',
        fontFamily: FONT,
        lineHeight: '1.6'
      }}>
        <strong style={{ color: T.textPrimary }}>Note:</strong> This tab shows all tournament registrations for {studentName}.
        {isAdmin
          ? ' You can approve, pay, or refund registrations from the Tournament Approvals section.'
          : ' Your parent can register you for available tournaments from the Parent Portal.'}
      </div>
    </div>
  );
};

export default TournamentStudentTab;
